/*
 * Dalfiori Universal AutoGift Engine
 * Configurable from the Cart drawer section > Regalos Dalfiori.
 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-df-auto-gift-root]';
  const DATA_SELECTOR = '[data-df-auto-gift-data]';
  const GIFT_PROPERTY = '_df_auto_gift';
  const RULE_PROPERTY = '_df_gift_rule';
  const VARIANT_PROPERTY = '_df_gift_variant';
  const CHOICE_PROPERTY = '_df_gift_choice';
  const BADGE_PROPERTY = '_df_gift_badge';
  const RULE_CHOICE_KEY_PREFIX = 'df-auto-gift:rule:';
  const VARIANT_CHOICE_KEY_PREFIX = 'df-auto-gift:variant:';
  const FAILURE_COOLDOWN = 60000;

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const normalizeOptionValue = (value) => normalize(value)
    .replace(/\b(tamano|talla|size)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const splitValues = (value) => String(value || '')
    .split(/[|,;\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);

  const parseMap = (value) => {
    const result = new Map();
    splitValues(value).forEach((entry) => {
      const match = entry.match(/^(.+?)(?:=>|=|:)(.+)$/);
      if (!match) return;
      result.set(normalizeOptionValue(match[1]), match[2].trim());
    });
    return result;
  };

  const optionValue = (options, name) => {
    const targetName = normalize(name);
    const option = (options || []).find((item) => normalize(item.name) === targetName);
    return option ? String(option.value || '') : '';
  };

  const routeWithJson = (route) => route.endsWith('.js') ? route : `${route}.js`;

  class DFAutoGiftEngine {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.data = this.readData(root);
      this.syncQueue = Promise.resolve({ changed: false, ok: true });
      this.failedRules = new Map();
      this.pendingRuleChoices = new Set();
      this.pendingVariantChoices = new Set();
      this.checkoutBypass = false;
      this.checkoutPending = false;
      this.refreshTimer = null;
      this.destroyed = false;
      this.bound = {};

      this.seedChoicesFromCart();
      this.bindEvents();
      this.refreshDynamicCheckout();
      this.reconcile({ reason: 'initial-load', announce: false, refresh: true })
        .then(() => this.renderChoiceControls());
    }

    get enabled() {
      return Boolean(this.data && this.data.settings && this.data.settings.enabled);
    }

    readData(container) {
      const script = container.querySelector(DATA_SELECTOR);
      if (!script) return { settings: { enabled: false }, rules: [], cartLines: [] };

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[Dalfiori AutoGift] Configuracion JSON invalida.', error);
        return { settings: { enabled: false }, rules: [], cartLines: [] };
      }
    }

    log(...args) {
      if (this.data?.settings?.debug) console.info('[Dalfiori AutoGift]', ...args);
    }

    bindEvents() {
      this.bound.cartAdd = () => this.scheduleReconcile('cart-add');
      this.bound.lineChange = () => this.scheduleReconcile('line-change');
      this.bound.drawerOpen = () => this.scheduleReconcile('drawer-open', 0);
      this.bound.variantChange = () => this.refreshDynamicCheckout();
      this.bound.pageShow = () => this.scheduleReconcile('page-show', 50);
      this.bound.visibility = () => {
        if (document.visibilityState === 'visible') this.scheduleReconcile('visibility', 100);
      };
      this.bound.checkout = this.handleCheckout.bind(this);
      this.bound.giftChoice = this.handleGiftChoice.bind(this);

      document.addEventListener('on:cart:add', this.bound.cartAdd);
      document.addEventListener('on:line-item:change', this.bound.lineChange);
      document.addEventListener('dispatch:cart-drawer:open', this.bound.drawerOpen);
      document.addEventListener('on:variant:change', this.bound.variantChange);
      document.addEventListener('submit', this.bound.checkout, true);
      document.addEventListener('change', this.bound.giftChoice);
      document.addEventListener('visibilitychange', this.bound.visibility);
      window.addEventListener('pageshow', this.bound.pageShow);
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.refreshTimer);
      this.clearDynamicCheckoutState();
      document.removeEventListener('on:cart:add', this.bound.cartAdd);
      document.removeEventListener('on:line-item:change', this.bound.lineChange);
      document.removeEventListener('dispatch:cart-drawer:open', this.bound.drawerOpen);
      document.removeEventListener('on:variant:change', this.bound.variantChange);
      document.removeEventListener('submit', this.bound.checkout, true);
      document.removeEventListener('change', this.bound.giftChoice);
      document.removeEventListener('visibilitychange', this.bound.visibility);
      window.removeEventListener('pageshow', this.bound.pageShow);
    }

    scheduleReconcile(reason, delay = 80) {
      if (!this.enabled || this.destroyed) return;
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => {
        this.reconcile({
          reason,
          announce: reason === 'cart-add',
          refresh: true
        });
      }, delay);
    }

    async loadSnapshot() {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('sections', this.sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`No fue posible leer las reglas (${response.status}).`);

      const sections = await response.json();
      const html = sections[this.sectionId];
      if (!html) throw new Error('Shopify no devolvio la seccion del motor de regalos.');

      const template = document.createElement('template');
      template.innerHTML = html;
      const freshRoot = template.content.querySelector(ROOT_SELECTOR);
      if (!freshRoot) throw new Error('No se encontro la configuracion actualizada del motor.');

      this.data = this.readData(freshRoot);
      this.seedChoicesFromCart();
      return this.data;
    }

    async getCart() {
      const response = await fetch(routeWithJson(theme.routes.cart), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`No fue posible leer el carrito (${response.status}).`);
      return response.json();
    }

    baseRuleMatches(rule, line) {
      if (!rule.enabled || line.autoGift) return false;

      const excludeToken = normalize(rule.buy.excludeToken);
      if (excludeToken) {
        const exclusionValues = [
          line.handle,
          ...(line.collections || []),
          ...(line.tags || [])
        ].map(normalize);
        if (exclusionValues.some((value) => value.includes(excludeToken))) return false;
      }

      let matches = false;
      const scope = rule.buy.scope;
      if (scope === 'product') {
        matches = Number(rule.buy.productId) > 0 && Number(rule.buy.productId) === Number(line.productId);
      } else if (scope === 'collection') {
        matches = Boolean(rule.buy.collectionHandle)
          && (line.collections || []).map(normalize).includes(normalize(rule.buy.collectionHandle));
      } else if (scope === 'tag') {
        matches = Boolean(rule.buy.tag)
          && (line.tags || []).map(normalize).includes(normalize(rule.buy.tag));
      } else if (scope === 'type') {
        matches = Boolean(rule.buy.type) && normalize(rule.buy.type) === normalize(line.type);
      } else if (scope === 'vendor') {
        matches = Boolean(rule.buy.vendor) && normalize(rule.buy.vendor) === normalize(line.vendor);
      }

      return matches;
    }

    ruleMatches(rule, line) {
      if (!this.baseRuleMatches(rule, line)) return false;

      const allowedValues = splitValues(rule.buy.optionValues);
      if (!rule.buy.optionName || allowedValues.length === 0) return true;

      const selectedValue = normalizeOptionValue(optionValue(line.options, rule.buy.optionName));
      return allowedValues.some((value) => normalizeOptionValue(value) === selectedValue);
    }

    getStoredChoice(prefix, key) {
      if (!key) return '';
      try {
        return window.sessionStorage.getItem(`${prefix}${key}`) || '';
      } catch (error) {
        this.log('No fue posible leer la preferencia del regalo.', error);
        return '';
      }
    }

    setStoredChoice(prefix, key, value) {
      if (!key) return;
      try {
        if (value) {
          window.sessionStorage.setItem(`${prefix}${key}`, String(value));
        } else {
          window.sessionStorage.removeItem(`${prefix}${key}`);
        }
      } catch (error) {
        this.log('No fue posible guardar la preferencia del regalo.', error);
      }
    }

    selectedRuleId(choiceGroup) {
      return this.getStoredChoice(RULE_CHOICE_KEY_PREFIX, choiceGroup);
    }

    selectedVariantId(ruleId) {
      return this.getStoredChoice(VARIANT_CHOICE_KEY_PREFIX, ruleId);
    }

    seedChoicesFromCart() {
      const lines = this.data?.cartLines || [];
      const rules = this.data?.rules || [];

      lines.filter((line) => line.autoGift && line.ruleId).forEach((line) => {
        const rule = rules.find((item) => item.id === line.ruleId);
        if (!rule) return;

        if (rule.choiceGroup && !this.pendingRuleChoices.has(rule.choiceGroup)) {
          this.setStoredChoice(RULE_CHOICE_KEY_PREFIX, rule.choiceGroup, rule.id);
        }
        if (rule.gift.variantMode === 'customer_choice'
          && line.giftVariantId
          && !this.pendingVariantChoices.has(rule.id)) {
          this.setStoredChoice(VARIANT_CHOICE_KEY_PREFIX, rule.id, line.giftVariantId);
        }
      });
    }

    availableVariantsForRule(rule, sourceValue = '') {
      let variants = (rule.gift.variants || []).filter((variant) => variant.available);
      if (!sourceValue || !rule.gift.targetOptionName) return variants;

      const normalizedSource = normalizeOptionValue(sourceValue);
      variants = variants.filter((variant) => {
        const candidate = optionValue(variant.options, rule.gift.targetOptionName);
        return normalizeOptionValue(candidate) === normalizedSource;
      });
      return variants;
    }

    resolveGiftVariant(rule, line) {
      const sourceValue = optionValue(line.options, rule.gift.sourceOptionName);
      const variants = rule.gift.variantMode === 'customer_choice'
        ? this.availableVariantsForRule(rule, sourceValue)
        : this.availableVariantsForRule(rule);
      if (variants.length === 0) return null;

      if (rule.gift.variantMode === 'fixed_id') {
        const fixed = variants.find((variant) => Number(variant.id) === Number(rule.gift.fixedVariantId));
        return fixed || null;
      }

      if (rule.gift.variantMode === 'same_option' || rule.gift.variantMode === 'map_option') {
        let targetValue = sourceValue;

        if (rule.gift.variantMode === 'map_option') {
          const valueMap = parseMap(rule.gift.optionMap);
          targetValue = valueMap.get(normalizeOptionValue(sourceValue)) || '';
        }

        if (!targetValue) return null;
        const normalizedTarget = normalizeOptionValue(targetValue);
        const matched = variants.find((variant) => {
          const candidate = optionValue(variant.options, rule.gift.targetOptionName);
          return normalizeOptionValue(candidate) === normalizedTarget;
        });
        return matched || null;
      }

      if (rule.gift.variantMode === 'customer_choice') {
        const selectedId = this.selectedVariantId(rule.id);
        const selected = variants.find((variant) => Number(variant.id) === Number(selectedId));
        return selected || variants[0];
      }

      return variants[0];
    }

    quantityFor(rule, line) {
      let quantity = Number(rule.quantity.fixed) || 1;

      if (rule.quantity.mode === 'option_map') {
        const sourceValue = optionValue(line.options, rule.quantity.optionName);
        const quantityMap = parseMap(rule.quantity.map);
        const mapped = Number(quantityMap.get(normalizeOptionValue(sourceValue)));
        if (Number.isFinite(mapped) && mapped >= 0) quantity = mapped;
      }

      quantity = Math.max(0, Math.floor(quantity));
      if (rule.quantity.basis === 'per_unit') quantity *= Math.max(1, Number(line.quantity) || 1);
      return quantity;
    }

    computeDesiredGifts() {
      const desired = new Map();
      const lines = this.data.cartLines || [];
      const rules = (this.data.rules || [])
        .filter((rule) => rule.enabled && Number(rule.gift.productId) > 0)
        .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));
      const claimedGroups = new Map();
      const appliedOnce = new Set();
      const unresolved = [];

      lines.forEach((line) => {
        if (line.autoGift) return;

        const matchingRules = rules
          .filter((rule) => this.ruleMatches(rule, line))
          .sort((a, b) => {
            if (a.choiceGroup && a.choiceGroup === b.choiceGroup) {
              const selectedId = this.selectedRuleId(a.choiceGroup);
              if (a.id === selectedId && b.id !== selectedId) return -1;
              if (b.id === selectedId && a.id !== selectedId) return 1;
            }
            return Number(b.priority || 0) - Number(a.priority || 0);
          });

        matchingRules.forEach((rule) => {
          if (rule.quantity.basis === 'once_per_order' && appliedOnce.has(rule.id)) return;

          const group = normalize(rule.exclusiveGroup);
          const lineClaims = claimedGroups.get(line.key) || new Set();
          if (group && lineClaims.has(group)) return;

          const variant = this.resolveGiftVariant(rule, line);
          if (!variant) {
            unresolved.push({ rule, line });
            return;
          }

          const quantity = this.quantityFor(rule, line);
          if (quantity <= 0) return;

          const desiredKey = `${rule.id}:${variant.id}`;
          const current = desired.get(desiredKey) || {
            key: desiredKey,
            rule,
            variant,
            quantity: 0
          };
          current.quantity += quantity;
          desired.set(desiredKey, current);

          if (group) {
            lineClaims.add(group);
            claimedGroups.set(line.key, lineClaims);
          }
          if (rule.quantity.basis === 'once_per_order') appliedOnce.add(rule.id);
        });
      });

      rules.forEach((rule) => {
        const max = Number(rule.quantity.max) || 0;
        if (max <= 0) return;

        let remaining = max;
        Array.from(desired.values())
          .filter((gift) => gift.rule.id === rule.id)
          .forEach((gift) => {
            gift.quantity = Math.min(gift.quantity, remaining);
            remaining -= gift.quantity;
            if (gift.quantity <= 0) desired.delete(gift.key);
          });
      });

      return { desired, unresolved };
    }

    giftKeyFromItem(item) {
      const properties = item.properties || {};
      if (String(properties[GIFT_PROPERTY]) !== 'true') return null;
      const ruleId = String(properties[RULE_PROPERTY] || '');
      const variantId = String(properties[VARIANT_PROPERTY] || item.variant_id || '');
      return ruleId && variantId ? `${ruleId}:${variantId}` : null;
    }

    isRuleCoolingDown(ruleId) {
      const failedAt = this.failedRules.get(ruleId);
      if (!failedAt) return false;
      if (Date.now() - failedAt > FAILURE_COOLDOWN) {
        this.failedRules.delete(ruleId);
        return false;
      }
      return true;
    }

    async changeLine(key, quantity) {
      const response = await fetch(routeWithJson(theme.routes.cartChange), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || `Error ${response.status}`);
      return data;
    }

    async addGift(gift) {
      const settings = this.data.settings;
      const properties = {
        [GIFT_PROPERTY]: 'true',
        [RULE_PROPERTY]: gift.rule.id,
        [VARIANT_PROPERTY]: String(gift.variant.id),
        [CHOICE_PROPERTY]: gift.rule.choiceLabel || gift.variant.title || '',
        [BADGE_PROPERTY]: settings.giftBadge || 'Regalo Dalfiori'
      };

      const response = await fetch(routeWithJson(theme.routes.cartAdd), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: Number(gift.variant.id), quantity: gift.quantity, properties }]
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || `Error ${response.status}`);
      return data;
    }

    renderMessage(template, gift) {
      return String(template || '')
        .replace(/(?:{{\s*gift\s*}}|\[\[\s*gift\s*\]\])/gi, gift.variant.productTitle || gift.rule.gift.title || 'regalo')
        .replace(/(?:{{\s*quantity\s*}}|\[\[\s*quantity\s*\]\])/gi, String(gift.quantity))
        .replace(/(?:{{\s*rule\s*}}|\[\[\s*rule\s*\]\])/gi, gift.rule.name || 'Promocion');
    }

    async performReconcile(options) {
      const result = {
        changed: false,
        ok: true,
        added: [],
        removed: [],
        errors: [],
        reason: options.reason || 'manual'
      };

      if (this.destroyed) return result;
      if (!this.enabled) return result;

      try {
        await this.loadSnapshot();
        this.refreshDynamicCheckout();
        if (!this.enabled) return result;

        let cart = await this.getCart();
        const { desired, unresolved } = this.computeDesiredGifts();
        const existing = new Map();

        cart.items.forEach((item) => {
          const giftKey = this.giftKeyFromItem(item);
          if (giftKey) existing.set(giftKey, item);
        });

        for (const [giftKey, item] of existing.entries()) {
          const target = desired.get(giftKey);
          const targetQuantity = target ? target.quantity : 0;
          if (Number(item.quantity) === Number(targetQuantity)) continue;

          try {
            await this.changeLine(item.key, targetQuantity);
            result.changed = true;
            if (targetQuantity === 0) result.removed.push(item.product_title);
          } catch (error) {
            result.ok = false;
            result.errors.push(error.message);
          }
        }

        for (const [giftKey, gift] of desired.entries()) {
          if (existing.has(giftKey) || this.isRuleCoolingDown(gift.rule.id)) continue;

          try {
            await this.addGift(gift);
            result.changed = true;
            result.added.push(gift);
          } catch (error) {
            this.failedRules.set(gift.rule.id, Date.now());
            result.ok = false;
            result.errors.push(`${gift.rule.name}: ${error.message}`);
          }
        }

        if (unresolved.length > 0) {
          unresolved.forEach(({ rule }) => {
            this.failedRules.set(rule.id, Date.now());
          });
          result.ok = false;
          result.errors.push(this.data.settings.variantErrorMessage);
        }

        if (this.data.settings.requireFree) {
          // Validate every managed gift on every pass. This also protects carts
          // that were created while a discount was active and reopened after it
          // expired, even when no quantity changed during this reconciliation.
          if (result.changed) cart = await this.getCart();
          const chargedGifts = cart.items.filter((item) => {
            const isGift = String((item.properties || {})[GIFT_PROPERTY]) === 'true';
            return isGift && Number(item.final_line_price) > 0;
          });

          for (const item of chargedGifts) {
            try {
              await this.changeLine(item.key, 0);
              const ruleId = String((item.properties || {})[RULE_PROPERTY] || '');
              if (ruleId) this.failedRules.set(ruleId, Date.now());
              result.changed = true;
            } catch (error) {
              result.errors.push(error.message);
            }
          }

          if (chargedGifts.length > 0) {
            result.ok = false;
            result.added = result.added.filter((gift) => (
              !chargedGifts.some((item) => Number(item.variant_id) === Number(gift.variant.id))
            ));
            result.errors.push(this.data.settings.discountErrorMessage);
          }
        }

        document.documentElement.classList.toggle(
          'df-auto-gift-cart-active',
          Boolean(this.data.settings.hideDynamicCheckout && desired.size > 0)
        );

        if (options.announce && result.added.length > 0 && result.errors.length === 0) {
          const messages = result.added.map((gift) => {
            const template = gift.rule.message || this.data.settings.successMessage;
            return this.renderMessage(template, gift);
          });
          this.showToast(messages.join(' '), 'success');
        } else if (result.errors.length > 0) {
          this.showToast(result.errors.filter(Boolean).join(' '), 'error');
        }

        if (options.refresh && result.changed) {
          await this.refreshCartUI();
        } else {
          this.renderChoiceControls();
        }
        this.log('Sincronizacion terminada', result);
        return result;
      } catch (error) {
        result.ok = false;
        result.errors.push(error.message);
        this.log('Error de sincronizacion', error);
        if (options.announce || options.reason === 'checkout') {
          this.showToast(this.data?.settings?.genericErrorMessage || error.message, 'error');
        }
        return result;
      }
    }

    reconcile(options = {}) {
      const normalizedOptions = {
        reason: options.reason || 'manual',
        announce: Boolean(options.announce),
        refresh: options.refresh !== false
      };

      this.syncQueue = this.syncQueue
        .catch(() => ({ changed: false, ok: false }))
        .then(() => this.performReconcile(normalizedOptions));
      return this.syncQueue;
    }

    async refreshCartUI() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh(false);
        this.renderChoiceControls();
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
        this.renderChoiceControls();
        return;
      }

      try {
        const response = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
        const sections = await response.json();
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = sections['cart-icon-bubble'];
      } catch (error) {
        this.log('No fue posible actualizar el contador del carrito.', error);
      }
      this.renderChoiceControls();
    }

    choiceRules(rule) {
      if (!rule?.choiceGroup) return [];
      return (this.data.rules || []).filter((candidate) => (
        candidate.enabled
        && candidate.choiceGroup === rule.choiceGroup
        && Number(candidate.gift.productId) > 0
        && (candidate.gift.variantMode === 'customer_choice'
          ? this.choiceVariants(candidate).length > 0
          : (candidate.gift.variants || []).some((variant) => variant.available))
      ));
    }

    choiceVariants(rule) {
      const allowedSizes = splitValues(rule.buy.optionValues).map(normalizeOptionValue);
      let variants = this.availableVariantsForRule(rule);

      if (allowedSizes.length > 0 && rule.gift.targetOptionName) {
        variants = variants.filter((variant) => {
          const size = optionValue(variant.options, rule.gift.targetOptionName);
          return allowedSizes.includes(normalizeOptionValue(size));
        });
      }

      const unique = new Map();
      variants.forEach((variant) => {
        const label = optionValue(variant.options, rule.gift.choiceOptionName)
          || variant.title
          || variant.productTitle;
        const key = normalizeOptionValue(label) || String(variant.id);
        const current = unique.get(key);
        if (!current || Number(variant.id) === Number(this.selectedVariantId(rule.id))) {
          unique.set(key, { ...variant, choiceLabel: label });
        }
      });
      return Array.from(unique.values());
    }

    appendChoiceField(host, labelText, select, helpText = '') {
      const field = document.createElement('div');
      field.className = 'df-auto-gift__choice-field';

      const label = document.createElement('label');
      label.className = 'df-auto-gift__choice-label';
      label.htmlFor = select.id;
      label.textContent = labelText;
      field.appendChild(label);
      field.appendChild(select);

      if (helpText) {
        const help = document.createElement('p');
        help.className = 'df-auto-gift__choice-help';
        help.textContent = helpText;
        field.appendChild(help);
      }

      host.appendChild(field);
    }

    renderChoiceControls() {
      if (!this.enabled) return;

      document.querySelectorAll('[data-df-gift-choice-host]').forEach((host, index) => {
        const line = host.closest('[data-df-auto-gift-line]');
        if (!line) return;

        const ruleId = line.dataset.dfGiftRule || '';
        const currentVariantId = line.dataset.dfGiftVariant || '';
        const rule = (this.data.rules || []).find((item) => item.id === ruleId);
        host.replaceChildren();
        if (!rule) return;

        const alternatives = this.choiceRules(rule);
        if (alternatives.length > 1) {
          const giftSelect = document.createElement('select');
          giftSelect.id = `df-gift-rule-${index}-${rule.id}`;
          giftSelect.className = 'df-auto-gift__select df-auto-gift__rule-select';
          giftSelect.dataset.choiceGroup = rule.choiceGroup;
          giftSelect.dataset.currentRuleId = rule.id;
          giftSelect.setAttribute('aria-label', 'Elige un regalo');

          alternatives.forEach((alternative) => {
            const option = document.createElement('option');
            option.value = alternative.id;
            option.textContent = alternative.choiceLabel || alternative.gift.title || alternative.name;
            option.selected = alternative.id === rule.id;
            giftSelect.appendChild(option);
          });

          this.appendChoiceField(
            host,
            'Elige un regalo',
            giftSelect,
            'Puedes cambiarlo sin costo mientras el coordinado permanezca en el carrito.'
          );
        }

        if (rule.gift.variantMode === 'customer_choice' && rule.gift.choiceOptionName) {
          const variants = this.choiceVariants(rule);
          if (variants.length === 0) return;

          const colorSelect = document.createElement('select');
          colorSelect.id = `df-gift-color-${index}-${rule.id}`;
          colorSelect.className = 'df-auto-gift__select df-auto-gift__color-select';
          colorSelect.dataset.ruleId = rule.id;
          colorSelect.dataset.currentVariantId = currentVariantId;
          colorSelect.setAttribute('aria-label', `Elige ${rule.gift.choiceOptionName.toLowerCase()}`);

          variants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = String(variant.id);
            option.textContent = variant.choiceLabel;
            option.selected = Number(variant.id) === Number(currentVariantId);
            colorSelect.appendChild(option);
          });

          this.appendChoiceField(
            host,
            `Elige ${rule.gift.choiceOptionName.toLowerCase()}`,
            colorSelect,
            'Solo se muestran variantes disponibles del tamaño de tu coordinado.'
          );
        }
      });
    }

    async verifyGiftSelection(ruleId, variantId = '') {
      const cart = await this.getCart();
      return cart.items.some((item) => {
        const properties = item.properties || {};
        const matchesRule = String(properties[GIFT_PROPERTY]) === 'true'
          && String(properties[RULE_PROPERTY]) === String(ruleId);
        const matchesVariant = !variantId || Number(item.variant_id) === Number(variantId);
        return matchesRule && matchesVariant && Number(item.final_line_price) === 0;
      });
    }

    async changeGiftRule(select) {
      const choiceGroup = select.dataset.choiceGroup || '';
      const previousRuleId = select.dataset.currentRuleId || '';
      const nextRuleId = select.value;
      if (!choiceGroup || !nextRuleId || nextRuleId === previousRuleId) return;

      select.disabled = true;
      this.pendingRuleChoices.add(choiceGroup);
      this.setStoredChoice(RULE_CHOICE_KEY_PREFIX, choiceGroup, nextRuleId);
      this.failedRules.delete(nextRuleId);

      await this.reconcile({ reason: 'gift-rule-choice', announce: false, refresh: true });
      const applied = await this.verifyGiftSelection(nextRuleId).catch(() => false);
      if (applied) {
        this.pendingRuleChoices.delete(choiceGroup);
        const selectedRule = (this.data.rules || []).find((rule) => rule.id === nextRuleId);
        this.showToast(`🎁 Elegiste ${selectedRule?.choiceLabel || 'tu regalo'}.`, 'success');
        return;
      }

      this.setStoredChoice(RULE_CHOICE_KEY_PREFIX, choiceGroup, previousRuleId);
      this.failedRules.delete(previousRuleId);
      this.failedRules.delete(nextRuleId);
      await this.reconcile({ reason: 'gift-rule-rollback', announce: false, refresh: true });
      this.pendingRuleChoices.delete(choiceGroup);
      this.showToast('No fue posible aplicar esa opción en $0. Conservamos tu regalo anterior.', 'error');
    }

    async changeGiftVariant(select) {
      const ruleId = select.dataset.ruleId || '';
      const previousVariantId = select.dataset.currentVariantId || '';
      const nextVariantId = select.value;
      if (!ruleId || !nextVariantId || nextVariantId === previousVariantId) return;

      select.disabled = true;
      this.pendingVariantChoices.add(ruleId);
      this.setStoredChoice(VARIANT_CHOICE_KEY_PREFIX, ruleId, nextVariantId);
      this.failedRules.delete(ruleId);

      await this.reconcile({ reason: 'gift-variant-choice', announce: false, refresh: true });
      const applied = await this.verifyGiftSelection(ruleId, nextVariantId).catch(() => false);
      if (applied) {
        this.pendingVariantChoices.delete(ruleId);
        const rule = (this.data.rules || []).find((item) => item.id === ruleId);
        const variant = (rule?.gift.variants || []).find((item) => Number(item.id) === Number(nextVariantId));
        const color = optionValue(variant?.options, rule?.gift.choiceOptionName) || variant?.title || 'el color elegido';
        this.showToast(`🎁 Color del regalo actualizado a ${color}.`, 'success');
        return;
      }

      this.setStoredChoice(VARIANT_CHOICE_KEY_PREFIX, ruleId, previousVariantId);
      this.failedRules.delete(ruleId);
      await this.reconcile({ reason: 'gift-variant-rollback', announce: false, refresh: true });
      this.pendingVariantChoices.delete(ruleId);
      this.showToast('Ese color no pudo aplicarse en $0. Conservamos la selección anterior.', 'error');
    }

    handleGiftChoice(event) {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement)) return;

      if (select.matches('.df-auto-gift__rule-select')) {
        this.changeGiftRule(select);
      } else if (select.matches('.df-auto-gift__color-select')) {
        this.changeGiftVariant(select);
      }
    }

    showToast(message, type = 'success') {
      if (!message || !this.data?.settings?.showToast) return;
      const toast = document.querySelector('[data-df-auto-gift-toast]');
      if (!toast) return;

      const text = toast.querySelector('[data-df-auto-gift-toast-text]');
      if (text) text.textContent = message;
      toast.dataset.type = type;
      toast.hidden = false;
      toast.classList.add('is-visible');

      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        window.setTimeout(() => { toast.hidden = true; }, 250);
      }, Number(this.data.settings.toastDuration) || 5000);
    }

    formProductData(productForm) {
      return {
        productId: Number(productForm.dataset.productId || 0),
        type: productForm.dataset.productType || '',
        vendor: productForm.dataset.productVendor || '',
        tags: splitValues(productForm.dataset.productTags || ''),
        collections: splitValues(productForm.dataset.productCollections || ''),
        options: [],
        autoGift: false
      };
    }

    clearDynamicCheckoutState() {
      document.documentElement.classList.remove('df-auto-gift-cart-active');
      document.querySelectorAll('.df-auto-gift-product-eligible').forEach((productForm) => {
        productForm.classList.remove('df-auto-gift-product-eligible');
      });
    }

    refreshDynamicCheckout() {
      if (!this.enabled || !this.data.settings.hideDynamicCheckout) {
        this.clearDynamicCheckoutState();
        return;
      }

      document.querySelectorAll('product-form[data-product-id]').forEach((productForm) => {
        const productData = this.formProductData(productForm);
        const eligible = (this.data.rules || []).some((rule) => (
          rule.enabled && this.baseRuleMatches(rule, productData)
        ));
        productForm.classList.toggle('df-auto-gift-product-eligible', eligible);
      });
    }

    async handleCheckout(event) {
      if (this.checkoutBypass || this.checkoutPending || !this.enabled) return;

      const submitter = event.submitter || document.activeElement;
      if (!submitter || submitter.name !== 'checkout') return;

      event.preventDefault();
      this.checkoutPending = true;
      const previousDisabled = submitter.disabled;
      submitter.disabled = true;
      submitter.classList.add('is-loading');

      try {
        await this.reconcile({ reason: 'checkout', announce: false, refresh: true });
        this.checkoutBypass = true;
        // A disabled submitter is not a successful form control and may lose
        // Shopify's `checkout` action. Restore it before the native submission.
        submitter.disabled = previousDisabled;
        submitter.classList.remove('is-loading');
        if (typeof event.target.requestSubmit === 'function') {
          event.target.requestSubmit(submitter);
        } else {
          event.target.submit();
        }
      } finally {
        window.setTimeout(() => { this.checkoutBypass = false; }, 0);
        this.checkoutPending = false;
        submitter.disabled = previousDisabled;
        submitter.classList.remove('is-loading');
      }
    }
  }

  const initialize = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    if (window.DFAutoGiftEngine && typeof window.DFAutoGiftEngine.destroy === 'function') {
      window.DFAutoGiftEngine.destroy();
    }
    window.DFAutoGiftEngine = new DFAutoGiftEngine(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector(ROOT_SELECTOR)) initialize();
  });
})();
